import each from 'lodash/each';
import filter from 'lodash/filter';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import size from 'lodash/size';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';

import type { UpdatePolicyInfoSteps } from '@jerry-serverless/enums/signup';

import { useUpdatePolicyInfo } from '../apollo/graphql/update_policy_info.mutation';
import type { AppData } from '../types/app_data';
import type { Car } from '../types/car';
import type { PolicyItem, SearchPolicyCar, SearchPolicyData, SearchPolicyDriver } from '../types/policy';
import { parseError, getClient } from '../apollo';
import type { User } from '../apollo/graphql/user.fragment';
import type { UpdateUserData, UpdateUserInput } from '../apollo/graphql/update_user.mutation';
import { SignupStep, UPDATE_USER_MUTATION } from '../apollo/graphql/update_user.mutation';

import { trackEvent } from './analytics';
import { unorderedArrayIsDiff } from './array';
import { getInitialCarsData, getLatestCars } from './car';

/**
 * Get the policy data from appData
 * @param appData AppData
 * @returns Searched policy data
 */
export const useSearchPolicyData: (appData: AppData) => SearchPolicyData | undefined = (appData) => {
  return useMemo(() => {
    // If policy data hasn't been sync'ed, use the info pulled from `searchPolicyInfo` query.
    if (!appData.syncedPolicyData) return getInitialCarsData(appData);

    // If it has been sync'ed, assemble data from user's drivers and cars.
    // NOTE: For drivers, the user's driver needs to be at the front.
    return {
      cars: getLatestCars(appData),
      drivers: sortBy(
        filter(appData.drivers || [], (drv) => !drv.isArchived),
        (drv) => drv.id !== appData.driverID
      ),
      coverages: [],
      isImported: false,
      isPredicted: appData.signupParams?.isPredicted || false,
    };
  }, [appData]);
};

/**
 * The cars need to confirm mileage:
 * 1. car which pulled from policy
 * 2. car which mileage is the default or isNil
 */
export const getNeedConfirmedMileageCars = (selectedCars?: SearchPolicyCar[]) =>
  filter(selectedCars, (car) => {
    return !car.userAdded;
  });

/**
 * This hook return the function to submit updatePolicyInfo
 * @param appData AppData
 * @param selectedCars User selected cars
 * @param updateFields Used to update updatedFieldsAt, for the pulled cars
 * @returns Submit function
 */
export const useConfirmCars = (appData: AppData, selectedCars: SearchPolicyCar[], updateFields?: string[]) => {
  const searchPolicyData = useSearchPolicyData(appData);
  const updatePolicyInfo = useUpdatePolicyInfo();

  const client = getClient();
  const [updateUser] = useMutation<UpdateUserData, UpdateUserInput>(UPDATE_USER_MUTATION, { client });

  /** Check if car list was updated */
  const carsUpdated = useMemo(() => {
    // From original car list, check if matching car object exists in selected cars.
    return unorderedArrayIsDiff(searchPolicyData?.cars || [], selectedCars, [
      'make',
      'model',
      'submodel',
      'year',
      'annualMileage',
      'ownership',
    ]);
  }, [searchPolicyData, selectedCars]);

  /** Check if driver list was updated */
  const driversUpdated = useMemo(() => {
    // From original driver list, check if matching driver object exists in selected drivers.
    return unorderedArrayIsDiff(searchPolicyData?.drivers || [], (appData.drivers || []) as SearchPolicyDriver[], [
      'ageLicensed',
      'dateOfBirth',
      'education',
      'gender',
      'maritalStatus',
      'name',
      'relationToUser',
      // NOTE: We don't allow DL number input right now, but when we do we'll need to enable this.
      // 'driverLicenseNumber'
    ]);
  }, [searchPolicyData, appData]);

  return useCallback(
    async (step?: UpdatePolicyInfoSteps) => {
      const policyItems: PolicyItem[] | undefined = searchPolicyData?.coverages || undefined;

      // Set 'isPredicted' to searched policy's flag or default to false.
      const isPredicted = searchPolicyData?.isPredicted || false;
      const { error, user } = await updatePolicyInfo({
        cars: selectedCars,
        maintenanceReminderData: appData.maintenanceReminderData,
        MaintenanceReminderSettledCars: appData.MaintenanceReminderSettledCars,
        // If this mutation is executed repeatedly, the driver added last time will be archived and updated to appData, so it is necessary to filter out the archived driver here.
        drivers: filter(appData.drivers, (driver) => !driver.isArchived) as SearchPolicyDriver[],
        driverViolations: appData.driverViolations,
        policyItems,
        options: {
          isPredicted,
          predictedEdited: false,
          useCustomPolicy: false, // TODO: how do we set this?
          policyStatus: searchPolicyData?.status || null,
          policyCarrier: searchPolicyData?.carrier || null,
          step,
        },
        updateFields,
      });

      // Track original # of cars displayed to user
      if (!appData.syncedPolicyData) {
        void trackEvent('OnboardingOriginalCarsCount', { eventLabel: `${searchPolicyData?.cars?.length || 0}` });
      }
      // Track # of cars submitted by user.
      const userAddedCarsNumber = size(filter(selectedCars, (selectedCar) => !!selectedCar.userAdded));
      void trackEvent('OnboardingConfirmedCarsCount', {
        eventLabel: `${selectedCars.length}`,
        eventMetadata: {
          selectedPulledCarsNumber: selectedCars.length - userAddedCarsNumber,
          totalPulledCarsNumber: searchPolicyData?.cars?.length || 0,
          userAddedCarsNumber,
        },
      });

      void trackEvent('OnboardingCars', { eventMetadata: { isOnboardingLite: !!appData.isOnboardingLite } });

      // Send trackevent if user edited cars or drivers.
      if (driversUpdated) void trackEvent('OnboardingDriversEdited');
      if (carsUpdated) void trackEvent('OnboardingCarsEdited');

      each(selectedCars, (car) => {
        const eventMetadata: Partial<Record<keyof SearchPolicyCar, number | string | boolean>> = omitBy(
          pick(car, ['annualMileage', 'usage', 'ridesharing', 'usedForDelivery', 'business', 'ownership']),
          (fieldVal) => isNil(fieldVal)
        );
        void trackEvent('OnboardingCarSelected', {
          eventLabel: car.userAdded ? 'User Added' : 'Pulled',
          eventMetadata,
        });
      });

      each(user?.cars?.edges || [], ({ node }) => {
        const eventMetadata: Partial<Record<keyof Car, number | string | boolean>> = omitBy(
          pick(node, ['annualMileage', 'usage', 'ridesharing', 'usedForDelivery', 'business', 'ownership']),
          (fieldVal) => isNil(fieldVal)
        );
        void trackEvent('OnboardingCarCreated', {
          eventMetadata: { car_id: node.id, ...eventMetadata },
        });
      });

      if (appData.selectedService?.refi) {
        await updateUser({
          variables: {
            input: {
              hasCarLoan: true,
              step: SignupStep.ownership,
            },
          },
        });
      }

      return { user, error };
    },
    [
      appData.MaintenanceReminderSettledCars,
      appData.driverViolations,
      appData.drivers,
      appData.isOnboardingLite,
      appData.maintenanceReminderData,
      appData.selectedService?.refi,
      appData.syncedPolicyData,
      carsUpdated,
      driversUpdated,
      searchPolicyData?.carrier,
      searchPolicyData?.cars?.length,
      searchPolicyData?.coverages,
      searchPolicyData?.isPredicted,
      searchPolicyData?.status,
      selectedCars,
      updateFields,
      updatePolicyInfo,
      updateUser,
    ]
  );
};

/**
 * Return a callback which receive a new app data to call confirmCars
 */
export const useConfirmCarsWithMergedAppData = (
  appData: AppData,
  selectedCars: SearchPolicyCar[],
  updateFields?: string[],
  step?: UpdatePolicyInfoSteps
) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [updating, setUpdating] = useState(false);
  const [newAppData, setNewAppData] = useState(appData);
  const confirmCars = useConfirmCars(newAppData, selectedCars, updateFields);

  const confirmCarsRef = useRef(confirmCars);
  confirmCarsRef.current = confirmCars;

  const callbackRef = useRef<(user?: User | null) => Promise<void>>();
  const mergedAppDataRef = useRef(newAppData);
  const shouldConfirmCarsRef = useRef(false);

  useEffect(() => {
    if (!shouldConfirmCarsRef.current) {
      return;
    }

    if (newAppData !== mergedAppDataRef.current) {
      return;
    }

    shouldConfirmCarsRef.current = false;

    void (async () => {
      try {
        setUpdating(true);
        setError?.(undefined);

        const { error, user } = await confirmCarsRef.current(step);

        if (error) {
          setError?.(error);
          setUpdating(false);
          return;
        }

        await callbackRef.current?.(user);
      } catch (err) {
        setError?.(parseError(err));
      } finally {
        setUpdating(false);
      }
    })();
  }, [newAppData, setError, step]);

  const confirmCarsWithMergedAppData = useCallback(
    (mergedAppData: AppData, callback: (user?: User | null) => Promise<void>) => {
      shouldConfirmCarsRef.current = true;
      mergedAppDataRef.current = mergedAppData;
      callbackRef.current = callback;
      setNewAppData(mergedAppData);
    },
    []
  );

  return { confirmCarsWithMergedAppData, updating, error };
};
